import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { ColumnAdapter } from '@makyoapp/makyo-common'
import { Copy16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonColor, ButtonVariant, Modal } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useReqError } from '@/hooks/useReqError'
import { generateColumnNameV2 } from '@/shared/ag-grid'

import { useGridV2Context } from '../../contexts/GridV2Context'

interface RelatedTableInfo {
  id: string
  name: string
  type: 'Snapshot' | 'Query' | 'Source'
}

export function GridDuplicateColumnModal({ column }: { column: ColumnAdapter }) {
  const printAPIError = useReqError()
  const { t } = useTranslation()

  const { dbClient } = useDbClientContext()
  const { activeTable, handleCloseMenu, handleDuplicateColumn } = useGridV2Context()

  const isSourceTableColumn = activeTable?.isSource

  const [isDupLoading, setIsDupLoading] = useState(false)

  // get affected tables
  const affectedTable = useMemo(() => {
    if (!dbClient || !activeTable || !column) return []

    let affectedTables: RelatedTableInfo[] = []

    if (activeTable.isQuery) {
      // find source table
      const sourceTable = activeTable.GetSnapshotSourceTable()
      if (sourceTable) {
        affectedTables = [
          {
            id: sourceTable.id,
            name: sourceTable.settings._Name,
            type: 'Source',
          },
        ]
      }
    }

    if (activeTable.isSource) {
      const queryTables = dbClient.queryRefMap.get(activeTable.id) ?? new Set()
      const snapshotTables = dbClient.snapshotRefMap.get(activeTable.id) ?? new Set()
      const relatedTables = Array.from([...queryTables, ...snapshotTables])

      affectedTables = relatedTables
        .map((id) => dbClient.tablesMap.get(id)!)
        .map((tb) => ({
          id: tb.id,
          name: tb.settings._Name,
          type: tb.isQuery ? 'Query' : 'Snapshot',
        }))
    }
    return affectedTables
    //
  }, [activeTable, column, dbClient])

  const handleDuplicate = () => {
    if (!activeTable || !column) return

    setIsDupLoading(true)

    const newColumnName = generateColumnNameV2(`${column.name} copy`, activeTable)

    handleDuplicateColumn(column.id, newColumnName)
      .catch(printAPIError)
      .finally(() => {
        setIsDupLoading(false)
        handleCloseMenu()
        toast.success(t('Column duplicated'))
      })
  }

  return (
    <div className='w-[360px] md:w-[580px] lg:w-[550px] mk-bg text-m-m'>
      <Modal.Header onCloseClick={handleCloseMenu}>
        <Trans>Confirmation</Trans>
      </Modal.Header>

      <div className='p-4 py-6 pb-8'>
        <Trans values={{ colName: column?.name }}>
          Are you sure you&apos;d like to duplicate <strong>{column?.name}</strong> column?
          {isSourceTableColumn ? (
            <>&nbsp; This will automatically duplicate the column to all derived query and snapshot tables.</>
          ) : (
            <>&nbsp; This will also duplicate the column in the source table.</>
          )}
        </Trans>

        {affectedTable.length > 0 && (
          <div className='mt-4 max-h-[200px] overflow-y-auto'>
            <span className='text-light'>Affected Tables:</span>
            <ul className='list-disc mt-2 pl-6 space-y-1'>
              {affectedTable.map((table) => (
                <li key={table.id}>
                  <b>{table.name}</b> <span className='text-light'>({table.type})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Modal.Footer className='justify-between'>
        <Button onClick={handleCloseMenu} variant={ButtonVariant.Outlined} colorStyle={ButtonColor.Secondary}>
          <Trans>Cancel</Trans>
        </Button>

        <Button
          icon={<Copy16 className='shrink-0' />}
          colorStyle={ButtonColor.Secondary}
          onClick={handleDuplicate}
          disabled={isDupLoading}
        >
          {isDupLoading ? <span className='loading spin' /> : <Trans>Yes, duplicate</Trans>}
        </Button>
      </Modal.Footer>
    </div>
  )
}
